"use client";

import { useVatPricing } from "@/hooks/useViewerVat";
import { useFormatPrice } from "@/contexts/CurrencyContext";

/**
 * The VAT line under a buyer-facing price.
 *
 * Prices shown to a viewer we charge VAT are VAT-inclusive (AssetPrice via
 * useVatPricing — Price Indication Directive 98/6/EC, prisinformationslagen),
 * so this line states what's in it: "Incl. 25% VAT (SE): $10.00 + $2.50 VAT".
 * Where we charge no VAT it says the price is what they pay, apart from any
 * VAT the checkout finds for their billing address.
 *
 * Pay-what-you-want (`pwyw`): the buyer types the amount before VAT, so the
 * line says VAT is added on top of it.
 *
 * useVatPricing is mount-gated, so the first client render matches the server.
 */
export function VatInclusiveNote({
  net,
  className,
  compact = false,
  pwyw = false,
}: {
  net: number;
  className?: string;
  /** Compact surfaces (mobile bar) show only "incl. VAT", and nothing when no VAT applies. */
  compact?: boolean;
  /** The amount is chosen by the buyer before VAT. */
  pwyw?: boolean;
}) {
  const vat = useVatPricing();
  const formatPrice = useFormatPrice();
  const cls = `text-xs text-muted-foreground ${className ?? ""}`.trim();

  if (pwyw) {
    if (!vat.applies) return null;
    return (
      <p className={cls}>
        {vat.ratePercent}% VAT ({vat.country}) is added at checkout to the amount you choose.
      </p>
    );
  }

  if (!(net > 0)) return null;

  if (vat.applies) {
    if (compact) return <p className={cls}>incl. VAT</p>;
    const gross = vat.gross(net);
    return (
      <p className={cls}>
        Incl. {vat.ratePercent}% VAT ({vat.country}): {formatPrice(net)} + {formatPrice(Math.round((gross - net) * 100) / 100)} VAT
      </p>
    );
  }

  if (compact) return null;
  return <p className={cls}>Any applicable VAT is calculated at checkout by your billing address</p>;
}
